/* eslint-disable @typescript-eslint/naming-convention */
import { type IKHRInteractivity_Declaration, type IKHRInteractivity_Graph, type IKHRInteractivity_Node } from "babylonjs-gltf2interface";
import { FlowGraphBlockNames } from "core/FlowGraph/Blocks/flowGraphBlockNames";
import { Logger } from "core/Misc/logger";
import { type ISerializedFlowGraphBlock, type ISerializedFlowGraphContext } from "core/FlowGraph/typeDefinitions";
import { type InteractivityEvent, type InteractivityGraphToFlowGraphParser } from "./interactivityGraphParser";
import { type IGLTF } from "../../glTFLoaderInterfaces";
import { FlowGraphTypes, getAnimationTypeByFlowGraphType } from "core/FlowGraph/flowGraphRichTypes";

// Per-block runaway-loop guard for KHR_interactivity for-loops. Interactivity assets may run large
// finite loops (e.g. the math/random Monte Carlo conformance asset iterates 10k times), so this is
// set above the FlowGraphForLoopBlock process-wide default while staying low enough that the guard is
// still meaningful — every iteration runs a full synchronous sub-graph, so a much larger value (and
// especially nested loops) could lock the main thread before the guard trips. Applied per for-loop
// block via the flow/for extraProcessor rather than by mutating the shared static.
const InteractivityForLoopMaxIterations = 20000;

/**
 * Describes how one KHR_interactivity configuration or socket property maps to FlowGraph.
 *
 * The same contract is used for fixed sockets, wildcard sockets, configuration validation,
 * defaulting, and multi-block routing during strict graph lowering.
 */
export interface IGLTFToFlowGraphMappingObject {
    /**
     * The name of the property in the FlowGraph block.
     */
    name: string;

    /**
     * Whether this socket mapping exists only for pre-ratification compatibility input.
     */
    compatibilityOnly?: boolean;

    /**
     * The type of the property in the glTF specs.
     * If not provided will be inferred.
     */
    gltfType?: string;

    /**
     * The type of the property in the FlowGraph block.
     * If not defined it equals the glTF type.
     */
    flowGraphType?: string;

    /**
     * A function that transforms the data from the glTF to the FlowGraph block.
     */
    dataTransformer?: (data: any, parser: InteractivityGraphToFlowGraphParser) => any;

    /**
     * If the property can contain multiple values.
     */
    isArray?: boolean;

    /**
     * If the property is in the options passed to the constructor of the block.
     */
    inOptions?: boolean;

    /**
     * If the property is an index to a value.
     * if defined this will be the name of the array to find the object in.
     */
    isVariable?: boolean;

    /**
     * the name of the class type this value will be mapped to.
     * This is used if we generate more than one block for a single glTF node.
     * Defaults to the first block in the mapping.
     */
    toBlock?: FlowGraphBlockNames | string;

    /**
     * Used in configuration values. If defined, this will be the default value, if no value is provided.
     */
    defaultValue?: any;

    /**
     * When the input value comes from a connection (an upstream node output) rather than a literal,
     * the parse-time {@link dataTransformer} cannot run. If this is `true`, the importer inserts a
     * runtime multiply block that scales the connected value by the animation target fps, converting
     * a KHR animation time (seconds) into Babylon animation frames. Literal values keep using the
     * {@link dataTransformer}. Used by `animation/start` / `animation/stopAt` time inputs, which may
     * be fed by a `pointer/get` (e.g. the read-only `maxTime` animation pointer).
     */
    convertConnectedTimeToFrames?: boolean;

    /**
     * KHR configuration value type used by canonical validation.
     */
    configurationType?: "bool" | "int" | "int[]" | "string";

    /**
     * Whether this configuration entry exists only for canonical validation.
     */
    validationOnly?: boolean;

    /**
     * Whether an operation without a complete default configuration requires this property.
     */
    required?: boolean;

    /**
     * Graph array referenced by an integer configuration value.
     */
    indexSource?: "types" | "variables" | "events" | "nodes" | "assetNodes";

    /**
     * Minimum number of values required for an array configuration.
     */
    minItems?: number;

    /**
     * Whether the configured indices generate required input value sockets.
     */
    generatesInputValueSockets?: boolean;

    /**
     * Whether integer case values generate required input value sockets.
     */
    generatesCaseInputValueSockets?: boolean;

    /**
     * Whether this string configuration generates JSON Pointer Template sockets.
     */
    pointerTemplate?: boolean;

    /**
     * Whether an invalid value falls back to the operation's default configuration.
     */
    invalidUsesDefault?: boolean;

    /**
     * Allowed KHR type signatures for a type-index configuration or an input value socket.
     */
    allowedSignatures?: readonly ("bool" | "float" | "float2" | "float3" | "float4" | "float2x2" | "float3x3" | "float4x4" | "int" | "ref" | "custom")[];

    /** Allowed literal values for this configuration property. */
    allowedValues?: readonly (boolean | number | string)[];

    /**
     * Input socket whose effective type determines this output socket type.
     */
    typeSourceInput?: string;

    /** Inclusive minimum for an integer configuration value. */
    minimum?: number;

    /** Inclusive maximum for an integer configuration value. */
    maximum?: number;

    /**
     * Whether this configuration value generates numbered input flow sockets.
     */
    generatesInputFlowSockets?: boolean;

    /**
     * Whether this configuration array generates output flow sockets.
     */
    generatesOutputFlowSockets?: boolean;

    /**
     * Properties in the same group fall back to their defaults together when any member is
     * missing or invalid.
     */
    configurationGroup?: string;

    /**
     * Whether duplicate array values are removed from the effective configuration.
     */
    uniqueValues?: boolean;

    /**
     * Whether a string configuration value is a `debug/log` message template.
     */
    debugLogTemplate?: boolean;
}

/**
 * Description of how a KHR_interactivity declaration (op such as
 * `pointer/get`, `event/onSelect`, `math/add`) maps to one or more
 * FlowGraph blocks. Used by {@link InteractivityGraphToFlowGraphParser}
 * to translate the source glTF graph into the serialized FlowGraph form.
 */
export interface IGLTFToFlowGraphMapping {
    /**
     * The type of the FlowGraph block(s).
     * Typically will be a single element in an array.
     * When adding blocks defined in this module use the KHR_interactivity prefix.
     */
    blocks: (FlowGraphBlockNames | string)[];

    /**
     * Exact value socket contract for an operation supplied by another extension.
     */
    declarationSchema?: {
        inputValueSockets: Record<string, "bool" | "float" | "float2" | "float3" | "float4" | "float2x2" | "float3x3" | "float4x4" | "int" | "ref" | "custom">;
        outputValueSockets: Record<string, "bool" | "float" | "float2" | "float3" | "float4" | "float2x2" | "float3x3" | "float4x4" | "int" | "ref" | "custom">;
    };
    /**
     * Additional exact declaration contracts supported for published legacy socket layouts.
     * Other mismatched declarations still lower to typed no-ops.
     */
    declarationSchemaVariants?: IGLTFToFlowGraphMapping["declarationSchema"][];
    /**
     * The inputs of the glTF node mapped to the FlowGraph block.
     */
    inputs?: {
        /**
         * The value inputs of the glTF node mapped to the FlowGraph block.
         */
        values?: { [originName: string]: IGLTFToFlowGraphMappingObject };
        /**
         * The flow inputs of the glTF node mapped to the FlowGraph block.
         */
        flows?: { [originName: string]: IGLTFToFlowGraphMappingObject };
    };
    /**
     * The outputs of the glTF node mapped to the FlowGraph block.
     */
    outputs?: {
        /**
         * The value outputs of the glTF node mapped to the FlowGraph block.
         */
        values?: { [originName: string]: IGLTFToFlowGraphMappingObject };
        /**
         * The flow outputs of the glTF node mapped to the FlowGraph block.
         */
        flows?: { [originName: string]: IGLTFToFlowGraphMappingObject };
    };
    /**
     * The configuration of the glTF node mapped to the FlowGraph block.
     * This information is usually passed to the constructor of the block.
     */
    configuration?: { [originName: string]: IGLTFToFlowGraphMappingObject };

    /**
     * If we generate more than one block for a single glTF node, this mapping will be used to map
     * between the flowGraph classes.
     */
    typeToTypeMapping?: { [originName: string]: IGLTFToFlowGraphMappingObject };

    /**
     * The connections between two or more blocks.
     * This is used to connect the blocks in the graph
     */
    interBlockConnectors?: {
        /**
         * The name of the input connection in the first block.
         */
        input: string;
        /**
         * The name of the output connection in the second block.
         */
        output: string;

        /**
         * The index of the block in the array of blocks that corresponds to the input.
         */
        inputBlockIndex: number;
        /**
         * The index of the block in the array of blocks that corresponds to the output.
         */
        outputBlockIndex: number;
        /**
         * If the connection is a variable connection or a flow connection.
         */
        isVariable?: boolean;
    }[];

    /**
     * This optional function will allow to validate the node, according to the glTF specs.
     * For example, if a node has a configuration object, it must be present and correct.
     * This is a basic node-based validation.
     * This function is expected to return false and log the error if the node is not valid.
     * Note that this function can also modify the node, if needed.
     *
     * @param gltfBlock the glTF node to validate
     * @param interactivityGraph the interactivity graph
     * @param glTFObject the glTF object
     * @returns true if validated, false if not.
     */
    validation?: (gltfBlock: IKHRInteractivity_Node, interactivityGraph: IKHRInteractivity_Graph, glTFObject?: IGLTF) => { valid: boolean; error?: string };

    /**
     * This is used if we need extra information for the constructor/options that is not provided directly by the glTF node.
     * This function can return more than one node, if extra nodes are needed for this block to function correctly.
     * Returning more than one block will usually happen when a json pointer was provided.
     *
     * @param gltfBlock the glTF node
     * @param declaration the declaration object
     * @param mapping the mapping object
     * @param parser the interactivity graph parser
     * @param serializedObjects the serialized objects
     * @param context the serialized flow graph context
     * @param globalGLTF the global gltf object
     * @returns an array of serialized nodes that will be added to the graph.
     */
    extraProcessor?: (
        gltfBlock: IKHRInteractivity_Node,
        declaration: IKHRInteractivity_Declaration,
        mapping: IGLTFToFlowGraphMapping,
        parser: InteractivityGraphToFlowGraphParser,
        serializedObjects: ISerializedFlowGraphBlock[],
        context: ISerializedFlowGraphContext,
        globalGLTF?: IGLTF
    ) => ISerializedFlowGraphBlock[];
}

export function getMappingForFullOperationName(fullOperationName: string) {
    const [op, extension] = fullOperationName.split(":");
    return getMappingForDeclaration({ op, extension });
}

/**
 * Returns whether a KHR operation accepts the conventional `in` flow socket when the registry
 * does not need a renamed or dynamic input mapping.
 * @param operation full KHR operation name
 * @returns true when the operation accepts the conventional input flow
 */
export function HasDefaultInteractivityFlowInput(operation: string): boolean {
    if (operation === "flow/waitAll") {
        return false;
    }
    return (
        operation.startsWith("flow/") ||
        operation.startsWith("animation/") ||
        operation === "pointer/set" ||
        operation === "pointer/interpolate" ||
        operation === "variable/set" ||
        operation === "variable/interpolate" ||
        operation === "event/send" ||
        operation === "event/stopPropagation" ||
        operation === "flow/log:BABYLON"
    );
}

/**
 * Normalizes serialized or runtime FlowGraph custom-event configuration for inverse comparison.
 * @param value serialized event-data entries or the runtime event-data dictionary
 * @returns stable event-data entries sorted by socket id
 */
export function NormalizeInteractivityEventDataConfiguration(value: unknown): unknown {
    const normalizeValue = (entry: unknown): unknown => {
        if (Array.isArray(entry)) {
            return entry.slice();
        }
        if (entry !== null && typeof entry === "object") {
            if (typeof (entry as { asArray?: () => ArrayLike<unknown> }).asArray === "function") {
                return Array.from((entry as { asArray: () => ArrayLike<unknown> }).asArray());
            }
            if (Object.prototype.hasOwnProperty.call(entry, "value")) {
                const nested = (entry as { value: unknown }).value;
                return Array.isArray(nested) ? nested.slice() : [nested];
            }
        }
        return [entry];
    };
    if (Array.isArray(value)) {
        return value
            .filter((entry): entry is { id: string; type: string; value?: unknown } => entry !== null && typeof entry === "object" && typeof entry.id === "string")
            .map((entry) => ({
                id: entry.id,
                type: entry.type,
                ...(entry.value === undefined ? {} : { value: normalizeValue(entry.value) }),
            }))
            .sort((left, right) => left.id.localeCompare(right.id));
    }
    if (value !== null && typeof value === "object") {
        return Object.entries(value as Record<string, unknown>)
            .map(([id, entry]) => {
                if (entry === null || typeof entry !== "object") {
                    return { id, malformed: true };
                }
                const definition = entry as { type?: { typeName?: string } | string; value?: unknown };
                return {
                    id,
                    type: typeof definition.type === "string" ? definition.type : definition.type?.typeName,
                    ...(definition.value === undefined ? {} : { value: normalizeValue(definition.value) }),
                };
            })
            .sort((left, right) => left.id.localeCompare(right.id));
    }
    return value;
}

export function getMappingForDeclaration(declaration: IKHRInteractivity_Declaration, returnNoOpIfNotAvailable: boolean = true): IGLTFToFlowGraphMapping | undefined {
    const extensionMappings = declaration.extension ? _GetOwnRegistryValue(gltfExtensionsToFlowGraphMapping, declaration.extension) : undefined;
    const mapping = declaration.extension ? _GetOwnRegistryValue(extensionMappings, declaration.op) : _GetOwnRegistryValue(gltfToFlowGraphMapping, declaration.op);
    if (!mapping) {
        if (returnNoOpIfNotAvailable) {
            Logger.Warn(`No mapping found for operation ${declaration.op} and extension ${declaration.extension || "KHR_interactivity"}`);
            return getNoOpMappingForDeclaration(declaration);
        }
    }
    return mapping;
}

/**
 * Creates the typed no-op mapping required for an unsupported extension declaration.
 * @param declaration unsupported extension declaration
 * @returns a mapping that preserves the declared value sockets
 */
export function getNoOpMappingForDeclaration(declaration: IKHRInteractivity_Declaration): IGLTFToFlowGraphMapping {
    const inputs: IGLTFToFlowGraphMapping["inputs"] = {};
    const outputs: IGLTFToFlowGraphMapping["outputs"] = {
        flows: {},
    };
    if (declaration.inputValueSockets) {
        inputs.values = Object.create(null);
        for (const key in declaration.inputValueSockets) {
            if (Object.prototype.hasOwnProperty.call(declaration.inputValueSockets, key)) {
                Object.defineProperty(inputs.values, key, {
                    configurable: true,
                    enumerable: true,
                    value: { name: key },
                    writable: true,
                });
            }
        }
    }
    if (declaration.outputValueSockets) {
        outputs.values = Object.create(null);
        Object.keys(declaration.outputValueSockets).forEach((key) => {
            Object.defineProperty(outputs.values!, key, {
                configurable: true,
                enumerable: true,
                value: { name: key },
                writable: true,
            });
        });
    }
    return {
        blocks: [],
        inputs,
        outputs,
    };
}

/**
 * This function will add new mapping to glTF interactivity.
 * Other extensions can define new types of blocks, this is the way to let interactivity know how to parse them.
 * @param key the type of node, i.e. "variable/get"
 * @param extension the extension of the interactivity operation, i.e. "KHR_selectability"
 * @param mapping The mapping object. See documentation or examples below.
 */
export function addNewInteractivityFlowGraphMapping(key: string, extension: string, mapping: IGLTFToFlowGraphMapping) {
    let extensionMappings = _GetOwnRegistryValue(gltfExtensionsToFlowGraphMapping, extension);
    if (!extensionMappings) {
        extensionMappings = Object.create(null);
        Object.defineProperty(gltfExtensionsToFlowGraphMapping, extension, {
            configurable: true,
            enumerable: true,
            value: extensionMappings,
            writable: true,
        });
    }
    Object.defineProperty(extensionMappings, key, {
        configurable: true,
        enumerable: true,
        value: mapping,
        writable: true,
    });
}

function _GetOwnRegistryValue<T>(registry: Record<string, T> | undefined, key: string): T | undefined {
    return registry && Object.prototype.hasOwnProperty.call(registry, key) ? registry[key] : undefined;
}

/**
 * One operation entry in the shared KHR_interactivity-to-FlowGraph registry.
 */
export interface IKHRInteractivityOperationRegistryEntry {
    /** Ratified operation identifier. */
    op: string;
    /** Extension defining the operation, when it is not a KHR_interactivity core operation. */
    extension?: string;
    /** Bidirectional structural mapping used by import and export. */
    mapping: IGLTFToFlowGraphMapping;
}

/**
 * Gets a deterministic snapshot of the registered KHR_interactivity operation mappings.
 * Export analysis uses this same registry as import lowering so the two directions cannot
 * independently drift.
 * @returns registered mappings sorted by extension and operation
 */
export function GetInteractivityOperationRegistry(): readonly IKHRInteractivityOperationRegistryEntry[] {
    const entries: IKHRInteractivityOperationRegistryEntry[] = Object.keys(gltfToFlowGraphMapping).map((op) => ({
        op,
        mapping: gltfToFlowGraphMapping[op],
    }));
    for (const extension of Object.keys(gltfExtensionsToFlowGraphMapping).sort()) {
        for (const op of Object.keys(gltfExtensionsToFlowGraphMapping[extension]).sort()) {
            entries.push({
                op,
                extension,
                mapping: gltfExtensionsToFlowGraphMapping[extension][op],
            });
        }
    }
    return entries.sort((left, right) => `${left.extension ?? ""}:${left.op}`.localeCompare(`${right.extension ?? ""}:${right.op}`));
}

const gltfExtensionsToFlowGraphMapping: { [extension: string]: { [key: string]: IGLTFToFlowGraphMapping } } = {
    /**
     * This is the BABYLON extension for glTF interactivity.
     * It defines babylon-specific blocks and operations.
     */
    BABYLON: {
        /**
         * flow/log is a flow node that logs input to the console.
         * It has "in" and "out" flows, and takes a message as input.
         * The message can be any type of value.
         * The message is logged to the console when the "in" flow is triggered.
         * The "out" flow is triggered when the message is logged.
         */
        "flow/log": {
            blocks: [FlowGraphBlockNames.ConsoleLog],
            inputs: {
                values: {
                    message: { name: "message" },
                    "[segment]": { name: "$1", compatibilityOnly: true },
                },
            },
            outputs: {
                flows: {
                    out: { name: "out", compatibilityOnly: true },
                },
            },
        },
    },
};

// this mapper is just a way to convert the glTF nodes to FlowGraph nodes in terms of input/output connection names and values.
const gltfToFlowGraphMapping: { [key: string]: IGLTFToFlowGraphMapping } = {
    "event/onStart": {
        blocks: [FlowGraphBlockNames.SceneReadyEvent],
        outputs: {
            values: {
                // KHR_interactivity `ref event` output (the event reference).
                event: { name: "event" },
            },
            flows: {
                out: { name: "done" },
            },
        },
    },
    "event/onTick": {
        blocks: [FlowGraphBlockNames.SceneTickEvent],
        inputs: {},
        outputs: {
            values: {
                timeSinceStart: { name: "timeSinceStart", gltfType: "number" },
                timeSinceLastTick: { name: "deltaTime", gltfType: "number" /*, dataTransformer: (time: number) => time / 1000*/ },
                // KHR_interactivity `ref event` output (the event reference).
                event: { name: "event" },
            },
            flows: {
                out: { name: "done" },
            },
        },
        extraProcessor(_node, _declaration, _mapping, _parser, blocks) {
            blocks[0].config.useFirstTickAsStart = true;
            return blocks;
        },
    },
    "event/send": {
        blocks: [FlowGraphBlockNames.SendCustomEvent],
        configuration: {
            event: {
                name: "eventId",
                configurationType: "int",
                required: true,
                indexSource: "events",
                validationOnly: true,
            },
        },
        inputs: {
            values: {
                "[segment]": { name: "$1" },
            },
        },
        outputs: {
            flows: {
                out: { name: "out" },
            },
        },
        extraProcessor(gltfBlock, declaration, _mapping, parser, serializedObjects) {
            // set eventId and eventData. The configuration object of the glTF should have a single object.
            // validate that we are running it on the right block.
            if (declaration.op !== "event/send" || !gltfBlock.configuration?.event) {
                throw new Error("Send event should have a single configuration object, the event itself");
            }
            const eventConfiguration = gltfBlock.configuration["event"];
            const eventId = eventConfiguration.value?.[0];
            if (typeof eventId !== "number") {
                throw new Error("Event id should be a number");
            }
            const event: InteractivityEvent = parser.arrays.events[eventId];
            const serializedObject = serializedObjects[0];
            serializedObject.config ||= {};
            serializedObject.config.eventId = event.eventId;
            serializedObject.config.eventData = event.eventData;
            return serializedObjects;
        },
    },
    "event/receive": {
        blocks: [FlowGraphBlockNames.ReceiveCustomEvent],
        configuration: {
            event: {
                name: "eventId",
                configurationType: "int",
                required: true,
                indexSource: "events",
                validationOnly: true,
            },
        },
        outputs: {
            values: {
                // KHR_interactivity `ref event` output (the event reference).
                event: { name: "event" },
                "[segment]": { name: "$1" },
            },
            flows: {
                out: { name: "done" },
            },
        },
        validation(gltfBlock, interactivityGraph) {
            if (!gltfBlock.configuration) {
                Logger.Error("Receive event should have a configuration object");
                return { valid: false, error: "Receive event should have a configuration object" };
            }
            const eventConfiguration = gltfBlock.configuration["event"];
            if (!eventConfiguration) {
                Logger.Error("Receive event should have a single configuration object, the event itself");
                return { valid: false, error: "Receive event should have a single configuration object, the event itself" };
            }
            const eventId = eventConfiguration.value?.[0];
            if (typeof eventId !== "number") {
                Logger.Error("Event id should be a number");
                return { valid: false, error: "Event id should be a number" };
            }
            const event = interactivityGraph.events?.[eventId];
            if (!event) {
                Logger.Error(`Event with id ${eventId} not found`);
                return { valid: false, error: `Event with id ${eventId} not found` };
            }
            return { valid: true };
        },
        extraProcessor(gltfBlock, declaration, _mapping, parser, serializedObjects) {
            // set eventId and eventData. The configuration object of the glTF should have a single object.
            // validate that we are running it on the right block.
            if (declaration.op !== "event/receive" || !gltfBlock.configuration?.event) {
                throw new Error("Receive event should have a single configuration object, the event itself");
            }
            const eventConfiguration = gltfBlock.configuration["event"];
            const eventId = eventConfiguration.value?.[0];
            if (typeof eventId !== "number") {
                throw new Error("Event id should be a number");
            }
            const event: InteractivityEvent = parser.arrays.events[eventId];
            const serializedObject = serializedObjects[0];
            serializedObject.config ||= {};
            serializedObject.config.eventId = event.eventId;
            serializedObject.config.eventData = event.eventData;
            return serializedObjects;
        },
    },
    "event/stopPropagation": {
        blocks: [FlowGraphBlockNames.StopEventPropagation],
        inputs: {
            values: {
                event: { name: "event" },
                stopImmediate: { name: "stopImmediate" },
            },
            flows: {
                in: { name: "in" },
            },
        },
        outputs: {
            flows: {
                out: { name: "out" },
            },
        },
    },
    "math/E": getSimpleInputMapping(FlowGraphBlockNames.E, []),
    "math/Pi": getSimpleInputMapping(FlowGraphBlockNames.PI, []),
    "math/Tau": getSimpleInputMapping(FlowGraphBlockNames.Tau, []),
    "math/Inf": getSimpleInputMapping(FlowGraphBlockNames.Inf, []),
    "math/NaN": getSimpleInputMapping(FlowGraphBlockNames.NaN, []),
    "math/abs": getSimpleInputMapping(FlowGraphBlockNames.Abs),
    "math/sign": getSimpleInputMapping(FlowGraphBlockNames.Sign),
    "math/trunc": getSimpleInputMapping(FlowGraphBlockNames.Trunc),
    "math/floor": getSimpleInputMapping(FlowGraphBlockNames.Floor),
    "math/ceil": getSimpleInputMapping(FlowGraphBlockNames.Ceil),
    "math/round": {
        blocks: [FlowGraphBlockNames.Round],
        configuration: {},
        inputs: {
            values: {
                a: { name: "a" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", typeSourceInput: "a" },
            },
        },
        extraProcessor(gltfBlock, declaration, _mapping, parser, serializedObjects) {
            // configure it to work the way glTF specifies
            serializedObjects[0].config ||= {};
            serializedObjects[0].config.roundHalfAwayFromZero = true;
            return serializedObjects;
        },
    },
    "math/fract": getSimpleInputMapping(FlowGraphBlockNames.Fraction),
    "math/neg": getSimpleInputMapping(FlowGraphBlockNames.Negation),
    "math/add": getSimpleInputMapping(FlowGraphBlockNames.Add, ["a", "b"], true),
    "math/sub": getSimpleInputMapping(FlowGraphBlockNames.Subtract, ["a", "b"], true),
    "math/mul": {
        blocks: [FlowGraphBlockNames.Multiply],
        inputs: {
            values: {
                a: { name: "a" },
                b: { name: "b" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", typeSourceInput: "a" },
            },
        },
        extraProcessor(_gltfBlock, _declaration, _mapping, _parser, serializedObjects) {
            // configure it to work the way glTF specifies
            serializedObjects[0].config ||= {};
            serializedObjects[0].config.useMatrixPerComponent = true;
            serializedObjects[0].config.preventIntegerFloatArithmetic = true;
            // try to infer the type or fallback to Integer
            // check the gltf block for the inputs, see if they have a type
            let type = -1;
            Object.keys(_gltfBlock.values || {}).find((value) => {
                if (_gltfBlock.values?.[value].type !== undefined) {
                    type = _gltfBlock.values[value].type;
                    return true;
                }
                return false;
            });
            if (type !== -1) {
                serializedObjects[0].config.type = _parser.arrays.types[type].flowGraphType;
            }
            return serializedObjects;
        },
        validation(gltfBlock) {
            if (gltfBlock.values) {
                // make sure types are the same
                return ValidateTypes(gltfBlock);
            }
            return { valid: true };
        },
    },
    "math/div": getSimpleInputMapping(FlowGraphBlockNames.Divide, ["a", "b"], true),
    "math/rem": getSimpleInputMapping(FlowGraphBlockNames.Modulo, ["a", "b"]),
    "math/min": getSimpleInputMapping(FlowGraphBlockNames.Min, ["a", "b"]),
    "math/max": getSimpleInputMapping(FlowGraphBlockNames.Max, ["a", "b"]),
    "math/clamp": getSimpleInputMapping(FlowGraphBlockNames.Clamp, ["a", "b", "c"]),
    "math/saturate": getSimpleInputMapping(FlowGraphBlockNames.Saturate),
    "math/mix": getSimpleInputMapping(FlowGraphBlockNames.MathInterpolation, ["a", "b", "c"]),
    // Smooth-step (Hermite interpolation): edges a/b and value c.
    "math/smoothStep": getSimpleInputMapping(FlowGraphBlockNames.SmoothStep, ["a", "b", "c"]),
    // Linear sRGB <-> OkLCh (Oklab polar form). Scalar r/g/b inputs map to l/c/h
    // outputs (hue in radians) and vice-versa.
    "math/rgbToOkLCh": {
        blocks: [FlowGraphBlockNames.RGBToOkLCh],
        inputs: {
            values: {
                r: { name: "r", gltfType: "number" },
                g: { name: "g", gltfType: "number" },
                b: { name: "b", gltfType: "number" },
            },
        },
        outputs: {
            values: {
                l: { name: "l" },
                c: { name: "c" },
                h: { name: "h" },
            },
        },
    },
    "math/rgbFromOkLCh": {
        blocks: [FlowGraphBlockNames.RGBFromOkLCh],
        inputs: {
            values: {
                l: { name: "l", gltfType: "number" },
                c: { name: "c", gltfType: "number" },
                h: { name: "h", gltfType: "number" },
            },
        },
        outputs: {
            values: {
                r: { name: "r" },
                g: { name: "g" },
                b: { name: "b" },
            },
        },
    },
    // Quaternion spherical-linear interpolation. Inputs are two unit
    // quaternions and an unclamped float coefficient.
    "math/quatSlerp": getSimpleInputMapping(FlowGraphBlockNames.MathSlerp, ["a", "b", "c"]),
    // Vector spherical-linear interpolation (float2/float3). Inputs are two
    // vectors and an unclamped float coefficient.
    "math/slerp": getSimpleInputMapping(FlowGraphBlockNames.VectorSlerp, ["a", "b", "c"]),
    "math/eq": getSimpleInputMapping(FlowGraphBlockNames.Equality, ["a", "b"]),
    // Reference equality. The spec defines `ref/eq` as: true if both refs are
    // null, true if both refer to the same object (regardless of whether it
    // exists), false otherwise. FlowGraphEqualityBlock falls through to a
    // strict `===` comparison for non-vector/matrix/numeric types, which
    // already produces the spec-defined behaviour for Babylon object refs.
    "ref/eq": getSimpleInputMapping(FlowGraphBlockNames.Equality, ["a", "b"]),
    "math/lt": getSimpleInputMapping(FlowGraphBlockNames.LessThan, ["a", "b"]),
    "math/le": getSimpleInputMapping(FlowGraphBlockNames.LessThanOrEqual, ["a", "b"]),
    "math/gt": getSimpleInputMapping(FlowGraphBlockNames.GreaterThan, ["a", "b"]),
    "math/ge": getSimpleInputMapping(FlowGraphBlockNames.GreaterThanOrEqual, ["a", "b"]),
    "math/isNaN": getSimpleInputMapping(FlowGraphBlockNames.IsNaN),
    "math/isInf": getSimpleInputMapping(FlowGraphBlockNames.IsInfinity),
    "math/select": {
        blocks: [FlowGraphBlockNames.Conditional],
        inputs: {
            values: {
                condition: { name: "condition" },
                // Should we validate those have the same type here, or assume it is already validated?
                a: { name: "onTrue" },
                b: { name: "onFalse" },
            },
        },
        outputs: {
            values: {
                value: { name: "output", typeSourceInput: "a" },
            },
        },
    },
    "math/random": {
        blocks: [FlowGraphBlockNames.Random],
        outputs: {
            values: {
                value: { name: "value", gltfType: "float" },
            },
        },
    },
    "math/sin": getSimpleInputMapping(FlowGraphBlockNames.Sin, ["a"], false, ["float", "float2", "float3", "float4"]),
    "math/cos": getSimpleInputMapping(FlowGraphBlockNames.Cos),
    "math/tan": getSimpleInputMapping(FlowGraphBlockNames.Tan),
    "math/asin": getSimpleInputMapping(FlowGraphBlockNames.Asin),
    "math/acos": getSimpleInputMapping(FlowGraphBlockNames.Acos),
    "math/atan": getSimpleInputMapping(FlowGraphBlockNames.Atan),
    "math/atan2": getSimpleInputMapping(FlowGraphBlockNames.Atan2, ["a", "b"]),
    "math/sinh": getSimpleInputMapping(FlowGraphBlockNames.Sinh),
    "math/cosh": getSimpleInputMapping(FlowGraphBlockNames.Cosh),
    "math/tanh": getSimpleInputMapping(FlowGraphBlockNames.Tanh),
    "math/asinh": getSimpleInputMapping(FlowGraphBlockNames.Asinh),
    "math/acosh": getSimpleInputMapping(FlowGraphBlockNames.Acosh),
    "math/atanh": getSimpleInputMapping(FlowGraphBlockNames.Atanh),
    "math/exp": getSimpleInputMapping(FlowGraphBlockNames.Exponential),
    "math/log": getSimpleInputMapping(FlowGraphBlockNames.Log),
    "math/log2": getSimpleInputMapping(FlowGraphBlockNames.Log2),
    "math/log10": getSimpleInputMapping(FlowGraphBlockNames.Log10),
    "math/sqrt": getSimpleInputMapping(FlowGraphBlockNames.SquareRoot),
    "math/cbrt": getSimpleInputMapping(FlowGraphBlockNames.CubeRoot),
    "math/pow": getSimpleInputMapping(FlowGraphBlockNames.Power, ["a", "b"]),
    "math/length": getSimpleInputMapping(FlowGraphBlockNames.Length),
    "math/normalize": {
        ...getSimpleInputMapping(FlowGraphBlockNames.Normalize),
        outputs: {
            values: {
                value: { name: "value", typeSourceInput: "a" },
                isValid: { name: "isValid", gltfType: "bool" },
            },
        },
    },
    "math/dot": getSimpleInputMapping(FlowGraphBlockNames.Dot, ["a", "b"]),
    "math/cross": getSimpleInputMapping(FlowGraphBlockNames.Cross, ["a", "b"]),
    "math/rotate2D": {
        blocks: [FlowGraphBlockNames.Rotate2D],
        inputs: {
            values: {
                a: { name: "a" },
                angle: { name: "b" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", typeSourceInput: "a" },
            },
        },
    },
    "math/rotate3D": {
        blocks: [FlowGraphBlockNames.Rotate3D],
        inputs: {
            values: {
                a: { name: "a" },
                rotation: { name: "b" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", typeSourceInput: "a" },
            },
        },
    },
    "math/transform": {
        // glTF transform is vectorN with matrixN
        blocks: [FlowGraphBlockNames.TransformVector],
        inputs: {
            values: {
                a: { name: "a" },
                b: { name: "b" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", typeSourceInput: "a" },
            },
        },
    },
    "math/combine2": {
        blocks: [FlowGraphBlockNames.CombineVector2],
        inputs: {
            values: {
                a: { name: "input_0", gltfType: "number" },
                b: { name: "input_1", gltfType: "number" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", gltfType: "float2" },
            },
        },
    },
    "math/combine3": {
        blocks: [FlowGraphBlockNames.CombineVector3],
        inputs: {
            values: {
                a: { name: "input_0", gltfType: "number" },
                b: { name: "input_1", gltfType: "number" },
                c: { name: "input_2", gltfType: "number" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", gltfType: "float3" },
            },
        },
    },
    "math/combine4": {
        blocks: [FlowGraphBlockNames.CombineVector4],
        inputs: {
            values: {
                a: { name: "input_0", gltfType: "number" },
                b: { name: "input_1", gltfType: "number" },
                c: { name: "input_2", gltfType: "number" },
                d: { name: "input_3", gltfType: "number" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", gltfType: "float4" },
            },
        },
    },
    // one input, N outputs! outputs named using numbers.
    "math/extract2": {
        blocks: [FlowGraphBlockNames.ExtractVector2],
        inputs: {
            values: {
                a: { name: "input", gltfType: "float2" },
            },
        },
        outputs: {
            values: {
                "0": { name: "output_0" },
                "1": { name: "output_1" },
            },
        },
    },
    "math/extract3": {
        blocks: [FlowGraphBlockNames.ExtractVector3],
        inputs: {
            values: {
                a: { name: "input", gltfType: "float3" },
            },
        },
        outputs: {
            values: {
                "0": { name: "output_0" },
                "1": { name: "output_1" },
                "2": { name: "output_2" },
            },
        },
    },
    "math/extract4": {
        blocks: [FlowGraphBlockNames.ExtractVector4],
        inputs: {
            values: {
                a: { name: "input", gltfType: "float4" },
            },
        },
        outputs: {
            values: {
                "0": { name: "output_0" },
                "1": { name: "output_1" },
                "2": { name: "output_2" },
                "3": { name: "output_3" },
            },
        },
    },
    "math/transpose": getSimpleInputMapping(FlowGraphBlockNames.Transpose, ["a"], false, ["float2x2", "float3x3", "float4x4"]),
    "math/determinant": getSimpleInputMapping(FlowGraphBlockNames.Determinant, ["a"], false, ["float2x2", "float3x3", "float4x4"]),
    "math/inverse": {
        ...getSimpleInputMapping(FlowGraphBlockNames.InvertMatrix, ["a"], false, ["float2x2", "float3x3", "float4x4"]),
        outputs: {
            values: {
                value: { name: "value", typeSourceInput: "a" },
                isValid: { name: "isValid", gltfType: "bool" },
            },
        },
    },
    "math/matMul": getSimpleInputMapping(FlowGraphBlockNames.MatrixMultiplication, ["a", "b"], true, ["float2x2", "float3x3", "float4x4"]),
    "math/matCompose": {
        blocks: [FlowGraphBlockNames.MatrixCompose],
        inputs: {
            values: {
                translation: { name: "position", gltfType: "float3" },
                rotation: { name: "rotationQuaternion", gltfType: "float4" },
                scale: { name: "scaling", gltfType: "float3" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", gltfType: "float4x4" },
            },
        },
        extraProcessor(_gltfBlock, _declaration, _mapping, _parser, serializedObjects, context) {
            // configure it to work the way glTF specifies
            const d = serializedObjects[0].dataInputs.find((input) => input.name === "rotationQuaternion");
            if (!d) {
                throw new Error("Rotation quaternion input not found");
            }
            // if value is defined, set the type to quaternion
            if (context._connectionValues[d.uniqueId]) {
                context._connectionValues[d.uniqueId].type = FlowGraphTypes.Quaternion;
            }
            return serializedObjects;
        },
    },
    "math/matDecompose": {
        blocks: [FlowGraphBlockNames.MatrixDecompose],
        inputs: {
            values: {
                a: { name: "input", gltfType: "float4x4" },
            },
        },
        outputs: {
            values: {
                translation: { name: "position" },
                rotation: { name: "rotationQuaternion" },
                scale: { name: "scaling" },
                scaling: { name: "scaling", compatibilityOnly: true },
                isValid: { name: "isValid", compatibilityOnly: true },
            },
        },
        extraProcessor(_gltfBlock, _declaration, _mapping, _parser, serializedObjects) {
            // KHR_interactivity step 4 of the matDecompose algorithm requires a non-decomposable matrix to still
            // report the extracted translation and the raw column lengths, instead of the type defaults the
            // FlowGraph block emits by default. The block has no `isValid` output in glTF, so the caller can only
            // observe those values.
            serializedObjects[0].config ||= {};
            serializedObjects[0].config.keepDegenerateComponents = true;
            return serializedObjects;
        },
    },
    "math/quatConjugate": getSimpleInputMapping(FlowGraphBlockNames.Conjugate, ["a"]),
    "math/quatMul": {
        blocks: [FlowGraphBlockNames.Multiply],
        inputs: {
            values: {
                a: { name: "a", gltfType: "float4" },
                b: { name: "b", gltfType: "float4" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", gltfType: "float4" },
            },
        },
        extraProcessor(_gltfBlock, _declaration, _mapping, _parser, serializedObjects) {
            serializedObjects[0].config ||= {};
            serializedObjects[0].config.type = FlowGraphTypes.Quaternion;
            return serializedObjects;
        },
    },
    "math/quatAngleBetween": getSimpleInputMapping(FlowGraphBlockNames.AngleBetween, ["a", "b"]),
    "math/quatFromAxisAngle": {
        blocks: [FlowGraphBlockNames.QuaternionFromAxisAngle],
        inputs: {
            values: {
                axis: { name: "a", gltfType: "float3" },
                angle: { name: "b", gltfType: "number" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", gltfType: "float4" },
            },
        },
    },
    "math/quatToAxisAngle": {
        blocks: [FlowGraphBlockNames.AxisAngleFromQuaternion],
        inputs: {
            values: {
                a: { name: "a", gltfType: "float4" },
            },
        },
        outputs: {
            values: {
                axis: { name: "axis", gltfType: "float3" },
                angle: { name: "angle", gltfType: "float" },
            },
        },
    },
    "math/quatFromDirections": {
        ...getSimpleInputMapping(FlowGraphBlockNames.QuaternionFromDirections, ["a", "b"]),
        outputs: {
            values: {
                value: { name: "value", gltfType: "float4" },
            },
        },
    },
    "math/quatFromUpForward": {
        blocks: [FlowGraphBlockNames.QuaternionFromUpForward],
        inputs: {
            values: {
                up: { name: "a", gltfType: "float3" },
                forward: { name: "b", gltfType: "float3" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", gltfType: "float4" },
            },
        },
    },
    // Tait–Bryan intrinsic Euler angles (x/y/z, in radians) to a rotation quaternion. The rotation
    // order is selected by the `order` configuration string (default `yxz`).
    "math/quatFromAngles": {
        blocks: [FlowGraphBlockNames.QuaternionFromAngles],
        configuration: {
            order: {
                name: "order",
                defaultValue: ["yxz"],
                configurationType: "string",
                invalidUsesDefault: true,
                allowedValues: ["xyz", "xzy", "yxz", "yzx", "zxy", "zyx"],
            },
        },
        inputs: {
            values: {
                x: { name: "a", gltfType: "number" },
                y: { name: "b", gltfType: "number" },
                z: { name: "c", gltfType: "number" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", gltfType: "float4" },
            },
        },
    },
    "math/combine2x2": {
        blocks: [FlowGraphBlockNames.CombineMatrix2D],
        inputs: {
            values: {
                a: { name: "input_0", gltfType: "number" },
                b: { name: "input_1", gltfType: "number" },
                c: { name: "input_2", gltfType: "number" },
                d: { name: "input_3", gltfType: "number" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", gltfType: "float2x2" },
            },
        },
    },
    "math/extract2x2": {
        blocks: [FlowGraphBlockNames.ExtractMatrix2D],
        inputs: {
            values: {
                a: { name: "input", gltfType: "float2x2" },
            },
        },
        outputs: {
            values: {
                "0": { name: "output_0" },
                "1": { name: "output_1" },
                "2": { name: "output_2" },
                "3": { name: "output_3" },
            },
        },
    },
    "math/combine3x3": {
        blocks: [FlowGraphBlockNames.CombineMatrix3D],
        inputs: {
            values: {
                a: { name: "input_0", gltfType: "number" },
                b: { name: "input_1", gltfType: "number" },
                c: { name: "input_2", gltfType: "number" },
                d: { name: "input_3", gltfType: "number" },
                e: { name: "input_4", gltfType: "number" },
                f: { name: "input_5", gltfType: "number" },
                g: { name: "input_6", gltfType: "number" },
                h: { name: "input_7", gltfType: "number" },
                i: { name: "input_8", gltfType: "number" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", gltfType: "float3x3" },
            },
        },
    },
    "math/extract3x3": {
        blocks: [FlowGraphBlockNames.ExtractMatrix3D],
        inputs: {
            values: {
                a: { name: "input", gltfType: "float3x3" },
            },
        },
        outputs: {
            values: {
                "0": { name: "output_0" },
                "1": { name: "output_1" },
                "2": { name: "output_2" },
                "3": { name: "output_3" },
                "4": { name: "output_4" },
                "5": { name: "output_5" },
                "6": { name: "output_6" },
                "7": { name: "output_7" },
                "8": { name: "output_8" },
            },
        },
    },
    "math/combine4x4": {
        blocks: [FlowGraphBlockNames.CombineMatrix],
        inputs: {
            values: {
                a: { name: "input_0", gltfType: "number" },
                b: { name: "input_1", gltfType: "number" },
                c: { name: "input_2", gltfType: "number" },
                d: { name: "input_3", gltfType: "number" },
                e: { name: "input_4", gltfType: "number" },
                f: { name: "input_5", gltfType: "number" },
                g: { name: "input_6", gltfType: "number" },
                h: { name: "input_7", gltfType: "number" },
                i: { name: "input_8", gltfType: "number" },
                j: { name: "input_9", gltfType: "number" },
                k: { name: "input_10", gltfType: "number" },
                l: { name: "input_11", gltfType: "number" },
                m: { name: "input_12", gltfType: "number" },
                n: { name: "input_13", gltfType: "number" },
                o: { name: "input_14", gltfType: "number" },
                p: { name: "input_15", gltfType: "number" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", gltfType: "float4x4" },
            },
        },
    },
    "math/extract4x4": {
        blocks: [FlowGraphBlockNames.ExtractMatrix],
        configuration: {},
        inputs: {
            values: {
                a: { name: "input", gltfType: "float4x4" },
            },
        },
        outputs: {
            values: {
                "0": { name: "output_0" },
                "1": { name: "output_1" },
                "2": { name: "output_2" },
                "3": { name: "output_3" },
                "4": { name: "output_4" },
                "5": { name: "output_5" },
                "6": { name: "output_6" },
                "7": { name: "output_7" },
                "8": { name: "output_8" },
                "9": { name: "output_9" },
                "10": { name: "output_10" },
                "11": { name: "output_11" },
                "12": { name: "output_12" },
                "13": { name: "output_13" },
                "14": { name: "output_14" },
                "15": { name: "output_15" },
            },
        },
    },
    "math/not": {
        blocks: [FlowGraphBlockNames.BitwiseNot],
        inputs: {
            values: {
                a: { name: "a" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", typeSourceInput: "a" },
            },
        },
        extraProcessor(_gltfBlock, _declaration, _mapping, _parser, serializedObjects, context) {
            // configure it to work the way glTF specifies
            serializedObjects[0].config ||= {};
            // try to infer the type or fallback to Integer
            const socketIn = serializedObjects[0].dataInputs[0];
            serializedObjects[0].config.valueType = context._connectionValues[socketIn.uniqueId]?.type ?? FlowGraphTypes.Integer;
            return serializedObjects;
        },
    },
    "math/and": {
        blocks: [FlowGraphBlockNames.BitwiseAnd],
        inputs: {
            values: {
                a: { name: "a" },
                b: { name: "b" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", typeSourceInput: "a" },
            },
        },
        extraProcessor(_gltfBlock, _declaration, _mapping, _parser, serializedObjects, context) {
            // configure it to work the way glTF specifies
            serializedObjects[0].config ||= {};
            // try to infer the type or fallback to Integer
            const socketInA = serializedObjects[0].dataInputs[0];
            const socketInB = serializedObjects[0].dataInputs[1];
            serializedObjects[0].config.valueType =
                context._connectionValues[socketInA.uniqueId]?.type ?? context._connectionValues[socketInB.uniqueId]?.type ?? FlowGraphTypes.Integer;
            return serializedObjects;
        },
    },
    "math/or": {
        blocks: [FlowGraphBlockNames.BitwiseOr],
        inputs: {
            values: {
                a: { name: "a" },
                b: { name: "b" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", typeSourceInput: "a" },
            },
        },
        extraProcessor(_gltfBlock, _declaration, _mapping, _parser, serializedObjects, context) {
            // configure it to work the way glTF specifies
            serializedObjects[0].config ||= {};
            // try to infer the type or fallback to Integer
            const socketInA = serializedObjects[0].dataInputs[0];
            const socketInB = serializedObjects[0].dataInputs[1];
            serializedObjects[0].config.valueType =
                context._connectionValues[socketInA.uniqueId]?.type ?? context._connectionValues[socketInB.uniqueId]?.type ?? FlowGraphTypes.Integer;
            return serializedObjects;
        },
    },
    "math/xor": {
        blocks: [FlowGraphBlockNames.BitwiseXor],
        inputs: {
            values: {
                a: { name: "a" },
                b: { name: "b" },
            },
        },
        outputs: {
            values: {
                value: { name: "value", typeSourceInput: "a" },
            },
        },
        extraProcessor(_gltfBlock, _declaration, _mapping, _parser, serializedObjects, context) {
            // configure it to work the way glTF specifies
            serializedObjects[0].config ||= {};
            // try to infer the type or fallback to Integer
            const socketInA = serializedObjects[0].dataInputs[0];
            const socketInB = serializedObjects[0].dataInputs[1];
            serializedObjects[0].config.valueType =
                context._connectionValues[socketInA.uniqueId]?.type ?? context._connectionValues[socketInB.uniqueId]?.type ?? FlowGraphTypes.Integer;
            return serializedObjects;
        },
    },
    "math/asr": getSimpleInputMapping(FlowGraphBlockNames.BitwiseRightShift, ["a", "b"]),
    "math/lsl": getSimpleInputMapping(FlowGraphBlockNames.BitwiseLeftShift, ["a", "b"]),
    "math/clz": getSimpleInputMapping(FlowGraphBlockNames.LeadingZeros),
    "math/ctz": getSimpleInputMapping(FlowGraphBlockNames.TrailingZeros),
    "math/popcnt": getSimpleInputMapping(FlowGraphBlockNames.OneBitsCounter),
    "math/rad": getSimpleInputMapping(FlowGraphBlockNames.DegToRad),
    "math/deg": getSimpleInputMapping(FlowGraphBlockNames.RadToDeg),
    "type/boolToInt": getSimpleInputMapping(FlowGraphBlockNames.BooleanToInt),
    "type/boolToFloat": getSimpleInputMapping(FlowGraphBlockNames.BooleanToFloat),
    "type/intToBool": getSimpleInputMapping(FlowGraphBlockNames.IntToBoolean),
    "type/intToFloat": getSimpleInputMapping(FlowGraphBlockNames.IntToFloat),
    "type/floatToInt": getSimpleInputMapping(FlowGraphBlockNames.FloatToInt),
    "type/floatToBool": getSimpleInputMapping(FlowGraphBlockNames.FloatToBoolean),

    // flows
    "flow/sequence": {
        blocks: [FlowGraphBlockNames.Sequence],
        outputs: {
            flows: {
                "[segment]": { name: "$1" },
            },
        },
        extraProcessor(gltfBlock, _declaration, _mapping, _arrays, serializedObjects) {
            const serializedObject = serializedObjects[0];
            serializedObject.config ||= {};
            serializedObject.config.outputSignalCount = Object.keys(gltfBlock.flows || []).length;
            serializedObject.signalOutputs.forEach((output, index) => {
                output.name = "out_" + index;
            });
            return serializedObjects;
        },
    },
    "flow/branch": {
        blocks: [FlowGraphBlockNames.Branch],
        inputs: {
            values: {
                condition: { name: "condition", gltfType: "boolean" },
            },
        },
        outputs: {
            flows: {
                true: { name: "onTrue" },
                false: { name: "onFalse" },
            },
        },
    },
    "flow/switch": {
        blocks: [FlowGraphBlockNames.Switch],
        configuration: {
            cases: {
                name: "cases",
                isArray: true,
                inOptions: true,
                defaultValue: [],
                configurationType: "int[]",
                invalidUsesDefault: true,
                generatesOutputFlowSockets: true,
                uniqueValues: true,
            },
        },
        inputs: {
            values: {
                selection: { name: "case", gltfType: "int" },
            },
        },
        outputs: {
            flows: {
                default: { name: "default" },
                "[segment]": { name: "$1" },
            },
        },
        validation(gltfBlock) {
            const cases = gltfBlock.configuration?.cases;
            if (cases && cases.value) {
                const onlyIntegers = cases.value.every(
                    (caseValue) => typeof caseValue === "number" && Number.isInteger(caseValue) && caseValue >= -2147483648 && caseValue <= 2147483647
                );
                if (!onlyIntegers) {
                    Logger.Warn("Switch cases should be integers. Using empty array instead.");
                    cases.value = [] as number[];
                    return { valid: true };
                }
                // check for duplicates
                const uniqueCases = new Set(cases.value);
                cases.value = Array.from(uniqueCases) as number[];
            }
            return { valid: true };
        },
        extraProcessor(_gltfBlock, _declaration, _mapping, _arrays, serializedObjects) {
            // convert all names of output flow to out_$1 apart from "default"
            const serializedObject = serializedObjects[0];
            serializedObject.signalOutputs.forEach((output) => {
                if (output.name !== "default") {
                    output.name = "out_" + output.name;
                }
            });
            return serializedObjects;
        },
    },
    "flow/while": {
        blocks: [FlowGraphBlockNames.WhileLoop],
        inputs: {
            values: {
                condition: { name: "condition", gltfType: "boolean" },
            },
        },
        outputs: {
            flows: {
                loopBody: { name: "executionFlow" },
                completed: { name: "completed" },
            },
        },
    },
    "flow/for": {
        blocks: [FlowGraphBlockNames.ForLoop],
        configuration: {
            initialIndex: {
                name: "initialIndex",
                gltfType: "int",
                inOptions: true,
                defaultValue: 0,
                configurationType: "int",
                invalidUsesDefault: true,
            },
        },
        inputs: {
            values: {
                startIndex: { name: "startIndex", gltfType: "int" },
                endIndex: { name: "endIndex", gltfType: "int" },
            },
        },
        outputs: {
            values: {
                index: { name: "index" },
            },
            flows: {
                loopBody: { name: "executionFlow" },
                completed: { name: "completed" },
            },
        },
        extraProcessor(_gltfBlock, _declaration, _mapping, _arrays, serializedObjects) {
            const serializedObject = serializedObjects[0];
            serializedObject.config ||= {};
            serializedObject.config.incrementIndexWhenLoopDone = true;
            // KHR_interactivity assets may run large finite loops (e.g. the math/random Monte Carlo
            // conformance asset iterates 10k times), well above the block's conservative default guard.
            // Raise the cap for interactivity for-loops only, per block, instead of mutating the
            // process-wide FlowGraphForLoopBlock.MaxLoopIterations that every other FlowGraph relies on.
            serializedObject.config.maxLoopIterations = InteractivityForLoopMaxIterations;
            return serializedObjects;
        },
    },
    "flow/doN": {
        blocks: [FlowGraphBlockNames.DoN],
        configuration: {},
        inputs: {
            values: {
                n: { name: "maxExecutions", gltfType: "int" },
            },
            flows: {
                reset: { name: "reset" },
            },
        },
        outputs: {
            flows: {
                out: { name: "out" },
            },
            values: {
                currentCount: { name: "executionCount" },
            },
        },
    },
    "flow/multiGate": {
        blocks: [FlowGraphBlockNames.MultiGate],
        inputs: {
            flows: {
                reset: { name: "reset" },
            },
        },
        configuration: {
            isRandom: {
                name: "isRandom",
                gltfType: "boolean",
                inOptions: true,
                defaultValue: false,
                configurationType: "bool",
                invalidUsesDefault: true,
                configurationGroup: "multiGate",
            },
            isLoop: {
                name: "isLoop",
                gltfType: "boolean",
                inOptions: true,
                defaultValue: false,
                configurationType: "bool",
                invalidUsesDefault: true,
                configurationGroup: "multiGate",
            },
        },
        outputs: {
            flows: {
                "[segment]": { name: "$1" },
            },
            values: {
                lastIndex: { name: "lastIndex", gltfType: "int" },
            },
        },
        extraProcessor(gltfBlock, declaration, _mapping, _arrays, serializedObjects) {
            if (declaration.op !== "flow/multiGate") {
                throw new Error("Invalid operation for MultiGate mapping");
            }
            const serializedObject = serializedObjects[0];
            serializedObject.config ||= {};
            serializedObject.config.outputSignalCount = Object.keys(gltfBlock.flows ?? {}).length;
            serializedObject.signalOutputs.forEach((output, index) => {
                output.name = "out_" + index;
            });
            return serializedObjects;
        },
    },
    "flow/waitAll": {
        blocks: [FlowGraphBlockNames.WaitAll],
        configuration: {
            inputFlows: {
                name: "inputSignalCount",
                gltfType: "number",
                inOptions: true,
                defaultValue: [0],
                configurationType: "int",
                invalidUsesDefault: true,
                minimum: 0,
                maximum: 64,
                generatesInputFlowSockets: true,
            },
        },
        inputs: {
            flows: {
                reset: { name: "reset" },
                "[segment]": { name: "in_$1" },
            },
        },
        outputs: {
            flows: {
                out: { name: "out" },
                completed: { name: "completed" },
            },
            values: {
                remainingInputs: { name: "remainingInputs", gltfType: "int" },
            },
        },
        validation(gltfBlock) {
            const inputFlowsConfiguration = gltfBlock.configuration?.inputFlows?.value;
            const inputFlows = inputFlowsConfiguration?.[0];
            if (inputFlowsConfiguration?.length !== 1 || typeof inputFlows !== "number" || !Number.isInteger(inputFlows) || inputFlows < 0 || inputFlows > 64) {
                gltfBlock.configuration = gltfBlock.configuration || {
                    inputFlows: { value: [0] },
                };
                gltfBlock.configuration.inputFlows.value = [0];
            }
            return { valid: true };
        },
    },
    "flow/throttle": {
        blocks: [FlowGraphBlockNames.Throttle],
        inputs: {
            values: {
                duration: { name: "duration", gltfType: "number" },
            },
            flows: {
                reset: { name: "reset" },
            },
        },
        outputs: {
            flows: {
                out: { name: "out" },
                err: { name: "error" },
            },
            values: {
                lastRemainingTime: { name: "lastRemainingTime", gltfType: "number" },
            },
        },
        extraProcessor(_node, _declaration, _mapping, _parser, blocks) {
            blocks[0].config.allowZeroDuration = true;
            return blocks;
        },
    },
    "flow/setDelay": {
        blocks: [FlowGraphBlockNames.SetDelay, "KHR_interactivity/FlowGraphDelayReferenceBlock"],
        inputs: {
            values: {
                duration: { name: "duration", gltfType: "number" },
            },
            flows: {
                cancel: { name: "cancel" },
            },
        },
        outputs: {
            flows: {
                out: { name: "out" },
                err: { name: "error" },
                done: { name: "done" },
            },
            values: {
                lastDelay: { name: "value", toBlock: "KHR_interactivity/FlowGraphDelayReferenceBlock" },
                lastDelayIndex: { name: "lastDelayIndex", compatibilityOnly: true },
            },
        },
        interBlockConnectors: [{ input: "input", output: "lastDelayIndex", inputBlockIndex: 1, outputBlockIndex: 0, isVariable: true }],
    },
    "flow/cancelDelay": {
        blocks: [FlowGraphBlockNames.CancelDelay, "KHR_interactivity/FlowGraphDelayReferenceBlock"],
        inputs: {
            values: {
                delay: { name: "input", gltfType: "ref", toBlock: "KHR_interactivity/FlowGraphDelayReferenceBlock" },
                delayIndex: { name: "input", compatibilityOnly: true, toBlock: "KHR_interactivity/FlowGraphDelayReferenceBlock" },
            },
        },
        interBlockConnectors: [{ input: "delayIndex", output: "value", inputBlockIndex: 0, outputBlockIndex: 1, isVariable: true }],
        extraProcessor(_node, _declaration, _mapping, _parser, blocks) {
            blocks[0].config.ignoreInvalidDelay = true;
            blocks[1].config.decode = true;
            return blocks;
        },
        outputs: {
            flows: {
                out: { name: "out" },
            },
        },
    },
    "variable/get": {
        blocks: [FlowGraphBlockNames.GetVariable],
        validation(gltfBlock) {
            if (!gltfBlock.configuration?.variable?.value) {
                Logger.Error("Variable get block should have a variable configuration");
                return { valid: false, error: "Variable get block should have a variable configuration" };
            }
            return { valid: true };
        },
        configuration: {
            variable: {
                name: "variable",
                gltfType: "number",
                configurationType: "int",
                required: true,
                indexSource: "variables",
                flowGraphType: "string",
                inOptions: true,
                isVariable: true,
                dataTransformer(index, parser) {
                    return parser.getVariableName(index);
                },
            },
        },
        outputs: {
            values: {
                value: { name: "value" },
            },
        },
    },
    "variable/set": {
        blocks: [FlowGraphBlockNames.SetVariable],
        inputs: {
            values: {
                "[segment]": { name: "$1" },
            },
        },
        configuration: {
            variables: {
                name: "variables",
                gltfType: "number",
                configurationType: "int[]",
                required: true,
                indexSource: "variables",
                minItems: 1,
                generatesInputValueSockets: true,
                flowGraphType: "string",
                inOptions: true,
                isArray: true,
                dataTransformer(index: number[], parser): string[] {
                    return index.map((i) => parser.getVariableName(i));
                },
            },
        },
        outputs: {
            flows: {
                out: { name: "out" },
            },
        },
        extraProcessor(_gltfBlock, _declaration, _mapping, parser, serializedObjects) {
            const serializedSetVariable = serializedObjects[0];
            serializedSetVariable.config.cancelVariableAnimations = true;
            serializedSetVariable.config.evaluateInputsBeforeWrite = true;
            serializedSetVariable.dataInputs.forEach((input) => {
                input.name = parser.getVariableName(+input.name);
            });

            return serializedObjects;
        },
    },
    "variable/interpolate": {
        blocks: [
            FlowGraphBlockNames.ValueInterpolation,
            FlowGraphBlockNames.Context,
            FlowGraphBlockNames.PlayAnimation,
            FlowGraphBlockNames.BezierCurveEasing,
            FlowGraphBlockNames.GetVariable,
        ],
        configuration: {
            variable: {
                name: "propertyName",
                configurationType: "int",
                required: true,
                indexSource: "variables",
                inOptions: true,
                isVariable: true,
                dataTransformer(index, parser) {
                    return parser.getVariableName(index);
                },
            },
            useSlerp: {
                name: "animationType",
                configurationType: "bool",
                required: true,
                inOptions: true,
                dataTransformer(value) {
                    return value === true ? FlowGraphTypes.Quaternion : undefined;
                },
            },
        },
        inputs: {
            values: {
                value: { name: "value_1" },
                duration: { name: "duration_1", gltfType: "number" },
                p1: { name: "controlPoint1", gltfType: "float2", toBlock: FlowGraphBlockNames.BezierCurveEasing },
                p2: { name: "controlPoint2", gltfType: "float2", toBlock: FlowGraphBlockNames.BezierCurveEasing },
            },
            flows: {
                in: { name: "in", toBlock: FlowGraphBlockNames.PlayAnimation },
            },
        },
        outputs: {
            flows: {
                err: { name: "error", toBlock: FlowGraphBlockNames.PlayAnimation },
                out: { name: "out", toBlock: FlowGraphBlockNames.PlayAnimation },
                done: { name: "done", toBlock: FlowGraphBlockNames.PlayAnimation },
            },
        },
        interBlockConnectors: [
            {
                input: "object",
                output: "userVariables",
                inputBlockIndex: 2,
                outputBlockIndex: 1,
                isVariable: true,
            },
            {
                input: "animation",
                output: "animation",
                inputBlockIndex: 2,
                outputBlockIndex: 0,
                isVariable: true,
            },
            {
                input: "easingFunction",
                output: "easingFunction",
                inputBlockIndex: 0,
                outputBlockIndex: 3,
                isVariable: true,
            },
            {
                input: "value_0",
                output: "value",
                inputBlockIndex: 0,
                outputBlockIndex: 4,
                isVariable: true,
            },
        ],
        extraProcessor(gltfBlock, _declaration, _mapping, parser, serializedObjects) {
            // is useSlerp is used, animationType should be set to be quaternion!
            const serializedValueInterpolation = serializedObjects[0];
            const propertyIndex = gltfBlock.configuration?.variable.value?.[0];
            if (typeof propertyIndex !== "number") {
                Logger.Error("Variable index is not defined for variable interpolation block");
                throw new Error("Variable index is not defined for variable interpolation block");
            }
            const variable = parser.arrays.staticVariables[propertyIndex];
            // if not set by useSlerp
            if (typeof serializedValueInterpolation.config?.animationType?.value === "undefined") {
                serializedValueInterpolation.config ||= {};
                serializedValueInterpolation.config.animationType ||= {};
                serializedValueInterpolation.config.animationType.value = getAnimationTypeByFlowGraphType(variable.type);
            }

            // variable/get configuration
            const serializedGetVariable = serializedObjects[4];
            serializedGetVariable.config ||= {};
            serializedGetVariable.config.variable ||= {};
            serializedGetVariable.config.variable.value = parser.getVariableName(propertyIndex);

            return serializedObjects;
        },
    },
    "pointer/get": {
        blocks: [FlowGraphBlockNames.GetProperty, FlowGraphBlockNames.JsonPointerParser],
        validation: (node) => ValidateJsonPointerTemplate(node),
        configuration: {
            pointer: { name: "jsonPointer", toBlock: FlowGraphBlockNames.JsonPointerParser, configurationType: "string", required: true, pointerTemplate: true },
            type: {
                name: "type",
                configurationType: "int",
                required: true,
                validationOnly: true,
                indexSource: "types",
            },
        },
        inputs: {
            values: {
                "[segment]": { name: "$1", toBlock: FlowGraphBlockNames.JsonPointerParser },
            },
        },
        outputs: {
            values: {
                value: { name: "value" },
                isValid: { name: "isValid", gltfType: "bool" },
            },
        },
        interBlockConnectors: [
            {
                input: "object",
                output: "object",
                inputBlockIndex: 0,
                outputBlockIndex: 1,
                isVariable: true,
            },
            {
                input: "propertyName",
                output: "propertyName",
                inputBlockIndex: 0,
                outputBlockIndex: 1,
                isVariable: true,
            },
            {
                input: "customGetFunction",
                output: "getFunction",
                inputBlockIndex: 0,
                outputBlockIndex: 1,
                isVariable: true,
            },
        ],
        extraProcessor(gltfBlock, _declaration, _mapping, parser, serializedObjects) {
            const configuredType = gltfBlock.configuration?.type?.value?.[0];
            if (typeof configuredType === "number" && parser.arrays.types[configuredType]) {
                serializedObjects[0].config.invalidValue = parser.getTypeDefault(configuredType);
            }
            serializedObjects.forEach((serializedObject) => {
                // check if it is the json pointer block
                if (serializedObject.className === FlowGraphBlockNames.JsonPointerParser) {
                    serializedObject.config ||= {};
                    serializedObject.config.outputValue = true;
                }
            });
            return serializedObjects;
        },
    },
    "pointer/set": {
        blocks: [FlowGraphBlockNames.SetProperty, FlowGraphBlockNames.JsonPointerParser],
        validation: (node) => ValidateJsonPointerTemplate(node, new Set(["value"])),
        configuration: {
            pointer: { name: "jsonPointer", toBlock: FlowGraphBlockNames.JsonPointerParser, configurationType: "string", required: true, pointerTemplate: true },
            type: {
                name: "type",
                configurationType: "int",
                required: true,
                validationOnly: true,
                indexSource: "types",
            },
        },
        inputs: {
            values: {
                // must be defined due to the array taking over
                value: { name: "value" },
                "[segment]": { name: "$1", toBlock: FlowGraphBlockNames.JsonPointerParser },
            },
        },
        outputs: {
            flows: {
                err: { name: "error" },
                out: { name: "out" },
            },
        },
        interBlockConnectors: [
            {
                input: "object",
                output: "object",
                inputBlockIndex: 0,
                outputBlockIndex: 1,
                isVariable: true,
            },
            {
                input: "propertyName",
                output: "propertyName",
                inputBlockIndex: 0,
                outputBlockIndex: 1,
                isVariable: true,
            },
            {
                input: "customSetFunction",
                output: "setFunction",
                inputBlockIndex: 0,
                outputBlockIndex: 1,
                isVariable: true,
            },
        ],
        extraProcessor(gltfBlock, _declaration, _mapping, parser, serializedObjects) {
            const typeIndex = gltfBlock.configuration?.type?.value?.[0];
            serializedObjects.forEach((serializedObject) => {
                // check if it is the json pointer block
                if (serializedObject.className === FlowGraphBlockNames.JsonPointerParser) {
                    serializedObject.config ||= {};
                    serializedObject.config.outputValue = true;
                    if (typeof typeIndex === "number") {
                        serializedObject.config.valueType = parser.arrays.types[typeIndex].flowGraphType;
                    }
                } else if (serializedObject.className === FlowGraphBlockNames.SetProperty) {
                    serializedObject.config.stopOnError = true;
                }
            });
            return serializedObjects;
        },
    },
    "pointer/interpolate": {
        // interpolate, parse the pointer and play the animation generated. 3 blocks!
        blocks: [FlowGraphBlockNames.ValueInterpolation, FlowGraphBlockNames.JsonPointerParser, FlowGraphBlockNames.PlayAnimation, FlowGraphBlockNames.BezierCurveEasing],
        validation: (node) => ValidateJsonPointerTemplate(node, new Set(["value", "duration", "p1", "p2"])),
        configuration: {
            pointer: { name: "jsonPointer", toBlock: FlowGraphBlockNames.JsonPointerParser, configurationType: "string", required: true, pointerTemplate: true },
            type: {
                name: "type",
                configurationType: "int",
                required: true,
                validationOnly: true,
                indexSource: "types",
                allowedSignatures: ["float", "float2", "float3", "float4", "float2x2", "float3x3", "float4x4"],
            },
        },
        inputs: {
            values: {
                value: { name: "value_1" },
                "[segment]": { name: "$1", toBlock: FlowGraphBlockNames.JsonPointerParser },
                duration: { name: "duration_1", gltfType: "number" /*, inOptions: true */ },
                p1: { name: "controlPoint1", gltfType: "float2", toBlock: FlowGraphBlockNames.BezierCurveEasing },
                p2: { name: "controlPoint2", gltfType: "float2", toBlock: FlowGraphBlockNames.BezierCurveEasing },
            },
            flows: {
                in: { name: "in", toBlock: FlowGraphBlockNames.PlayAnimation },
            },
        },
        outputs: {
            flows: {
                err: { name: "error", toBlock: FlowGraphBlockNames.PlayAnimation },
                out: { name: "out", toBlock: FlowGraphBlockNames.PlayAnimation },
                done: { name: "done", toBlock: FlowGraphBlockNames.PlayAnimation },
            },
        },
        interBlockConnectors: [
            {
                input: "object",
                output: "object",
                inputBlockIndex: 2,
                outputBlockIndex: 1,
                isVariable: true,
            },
            {
                input: "propertyName",
                output: "propertyName",
                inputBlockIndex: 0,
                outputBlockIndex: 1,
                isVariable: true,
            },
            {
                input: "customBuildAnimation",
                output: "generateAnimationsFunction",
                inputBlockIndex: 0,
                outputBlockIndex: 1,
                isVariable: true,
            },
            {
                input: "animation",
                output: "animation",
                inputBlockIndex: 2,
                outputBlockIndex: 0,
                isVariable: true,
            },
            {
                input: "animationTargets",
                output: "animationTargets",
                inputBlockIndex: 2,
                outputBlockIndex: 1,
                isVariable: true,
            },
            {
                input: "easingFunction",
                output: "easingFunction",
                inputBlockIndex: 0,
                outputBlockIndex: 3,
                isVariable: true,
            },
            {
                input: "value_0",
                output: "value",
                inputBlockIndex: 0,
                outputBlockIndex: 1,
                isVariable: true,
            },
        ],
        extraProcessor(gltfBlock, _declaration, _mapping, parser, serializedObjects) {
            const typeIndex = gltfBlock.configuration?.type?.value?.[0];
            serializedObjects.forEach((serializedObject) => {
                // check if it is the json pointer block
                if (serializedObject.className === FlowGraphBlockNames.JsonPointerParser) {
                    serializedObject.config ||= {};
                    serializedObject.config.outputValue = true;
                    if (typeof typeIndex === "number") {
                        serializedObject.config.valueType = parser.arrays.types[typeIndex].flowGraphType;
                    }
                } else if (serializedObject.className === FlowGraphBlockNames.ValueInterpolation) {
                    serializedObject.config ||= {};
                    if (typeof typeIndex === "number") {
                        serializedObject.config.animationType = parser.arrays.types[typeIndex].flowGraphType;
                    }
                }
            });
            return serializedObjects;
        },
    },
    "animation/start": {
        blocks: [FlowGraphBlockNames.PlayAnimation, FlowGraphBlockNames.ArrayIndex, "KHR_interactivity/FlowGraphGLTFDataProvider"],
        inputs: {
            values: {
                animation: { name: "index", gltfType: "ref", toBlock: FlowGraphBlockNames.ArrayIndex },
                speed: { name: "speed", gltfType: "number" },
                startTime: {
                    name: "from",
                    gltfType: "number",
                    convertConnectedTimeToFrames: true,
                    dataTransformer: (time: number[], parser) => [time[0] * parser._animationTargetFps],
                },
                endTime: {
                    name: "to",
                    gltfType: "number",
                    convertConnectedTimeToFrames: true,
                    dataTransformer: (time: number[], parser) => [time[0] * parser._animationTargetFps],
                },
            },
        },
        outputs: {
            flows: {
                out: { name: "out" },
                err: { name: "error" },
                done: { name: "done" },
            },
        },
        interBlockConnectors: [
            {
                input: "animationGroup",
                output: "value",
                inputBlockIndex: 0,
                outputBlockIndex: 1,
                isVariable: true,
            },
            {
                input: "array",
                output: "animationGroups",
                inputBlockIndex: 1,
                outputBlockIndex: 2,
                isVariable: true,
            },
        ],
        extraProcessor(_gltfBlock, _declaration, _mapping, _arrays, serializedObjects, _context, globalGLTF) {
            const playAnimationBlock = serializedObjects.find((block) => block.className === FlowGraphBlockNames.PlayAnimation);
            if (playAnimationBlock) {
                playAnimationBlock.config.useVirtualTimeline = true;
            }
            const arrayIndexBlock = serializedObjects.find((block) => block.className === FlowGraphBlockNames.ArrayIndex);
            if (arrayIndexBlock) {
                arrayIndexBlock.config.referenceCollection = "animations";
            }
            // add the glTF to the configuration of the last serialized object
            const serializedObject = serializedObjects[serializedObjects.length - 1];
            serializedObject.config ||= {};
            serializedObject.config.glTF = globalGLTF;
            return serializedObjects;
        },
    },
    "animation/stop": {
        blocks: [FlowGraphBlockNames.StopAnimation, FlowGraphBlockNames.ArrayIndex, "KHR_interactivity/FlowGraphGLTFDataProvider"],
        inputs: {
            values: {
                animation: { name: "index", gltfType: "ref", toBlock: FlowGraphBlockNames.ArrayIndex },
            },
        },
        outputs: {
            flows: {
                out: { name: "out" },
                err: { name: "error" },
            },
        },
        interBlockConnectors: [
            {
                input: "animationGroup",
                output: "value",
                inputBlockIndex: 0,
                outputBlockIndex: 1,
                isVariable: true,
            },
            {
                input: "array",
                output: "animationGroups",
                inputBlockIndex: 1,
                outputBlockIndex: 2,
                isVariable: true,
            },
        ],
        extraProcessor(_gltfBlock, _declaration, _mapping, _arrays, serializedObjects, _context, globalGLTF) {
            const stopAnimationBlock = serializedObjects.find((block) => block.className === FlowGraphBlockNames.StopAnimation);
            if (stopAnimationBlock) {
                stopAnimationBlock.config.skipOnAnimationEnd = true;
            }
            const arrayIndexBlock = serializedObjects.find((block) => block.className === FlowGraphBlockNames.ArrayIndex);
            if (arrayIndexBlock) {
                arrayIndexBlock.config.referenceCollection = "animations";
            }
            // add the glTF to the configuration of the last serialized object
            const serializedObject = serializedObjects[serializedObjects.length - 1];
            serializedObject.config ||= {};
            serializedObject.config.glTF = globalGLTF;
            return serializedObjects;
        },
    },
    "animation/stopAt": {
        blocks: [FlowGraphBlockNames.StopAnimation, FlowGraphBlockNames.ArrayIndex, "KHR_interactivity/FlowGraphGLTFDataProvider"],
        configuration: {},
        inputs: {
            values: {
                animation: { name: "index", gltfType: "ref", toBlock: FlowGraphBlockNames.ArrayIndex },
                stopTime: {
                    name: "stopAtFrame",
                    gltfType: "number",
                    convertConnectedTimeToFrames: true,
                    dataTransformer: (time: number[], parser) => [time[0] * parser._animationTargetFps],
                },
            },
        },
        outputs: {
            flows: {
                out: { name: "out" },
                err: { name: "error" },
                done: { name: "done" },
            },
        },
        interBlockConnectors: [
            {
                input: "animationGroup",
                output: "value",
                inputBlockIndex: 0,
                outputBlockIndex: 1,
                isVariable: true,
            },
            {
                input: "array",
                output: "animationGroups",
                inputBlockIndex: 1,
                outputBlockIndex: 2,
                isVariable: true,
            },
        ],
        extraProcessor(_gltfBlock, _declaration, _mapping, _arrays, serializedObjects, _context, globalGLTF) {
            const stopAnimationBlock = serializedObjects.find((block) => block.className === FlowGraphBlockNames.StopAnimation);
            if (stopAnimationBlock) {
                stopAnimationBlock.config.useVirtualStopAt = true;
                stopAnimationBlock.config.skipOnAnimationEnd = true;
            }
            const arrayIndexBlock = serializedObjects.find((block) => block.className === FlowGraphBlockNames.ArrayIndex);
            if (arrayIndexBlock) {
                arrayIndexBlock.config.referenceCollection = "animations";
            }
            // add the glTF to the configuration of the last serialized object
            const serializedObject = serializedObjects[serializedObjects.length - 1];
            serializedObject.config ||= {};
            serializedObject.config.glTF = globalGLTF;
            return serializedObjects;
        },
    },
    "math/switch": {
        blocks: [FlowGraphBlockNames.DataSwitch],
        configuration: {
            cases: {
                name: "cases",
                isArray: true,
                inOptions: true,
                defaultValue: [],
                configurationType: "int[]",
                invalidUsesDefault: true,
                generatesCaseInputValueSockets: true,
                uniqueValues: true,
            },
        },
        inputs: {
            values: {
                selection: { name: "case", gltfType: "int" },
                default: { name: "default" },
                "[case]": { name: "$1" },
            },
        },
        outputs: {
            values: {
                value: { name: "value" },
            },
        },
        validation(gltfBlock) {
            const cases = gltfBlock.configuration?.cases;
            if (cases && cases.value) {
                const onlyIntegers = cases.value.every((caseValue) => {
                    // case value should be an integer. Since Number.isInteger(1.0) is true, we need to check if toString has only digits.
                    return typeof caseValue === "number" && /^-?\d+$/.test(caseValue.toString());
                });
                if (!onlyIntegers) {
                    Logger.Warn("Switch cases should be integers. Using empty array instead.");
                    cases.value = [] as number[];
                    return { valid: true };
                }
                // check for duplicates
                const uniqueCases = new Set(cases.value);
                cases.value = Array.from(uniqueCases) as number[];
            }
            return { valid: true };
        },
        extraProcessor(_gltfBlock, _declaration, _mapping, _arrays, serializedObjects) {
            const serializedObject = serializedObjects[0];
            serializedObject.dataInputs.forEach((input) => {
                if (input.name !== "default" && input.name !== "case") {
                    input.name = "in_" + input.name;
                }
            });
            serializedObject.config ||= {};
            serializedObject.config.treatCasesAsIntegers = true;
            return serializedObjects;
        },
    },
    "debug/log": {
        blocks: [FlowGraphBlockNames.ConsoleLog],
        inputs: {
            flows: {
                in: { name: "in" },
            },
            values: {
                "[parameter]": { name: "$1" },
            },
        },
        outputs: {
            flows: {
                out: { name: "out" },
            },
        },
        configuration: {
            severity: {
                name: "severity",
                configurationType: "int",
                defaultValue: 0,
                invalidUsesDefault: true,
                validationOnly: true,
                configurationGroup: "debugLog",
            },
            message: {
                name: "messageTemplate",
                configurationType: "string",
                defaultValue: "",
                invalidUsesDefault: true,
                inOptions: true,
                configurationGroup: "debugLog",
                debugLogTemplate: true,
            },
        },
    },
};

function getSimpleInputMapping(
    type: FlowGraphBlockNames,
    inputs: string[] = ["a"],
    inferType?: boolean,
    allowedSignatures?: IGLTFToFlowGraphMappingObject["allowedSignatures"]
): IGLTFToFlowGraphMapping {
    return {
        blocks: [type],
        inputs: {
            values: inputs.reduce(
                (acc, input) => {
                    acc[input] = { name: input, allowedSignatures };
                    return acc;
                },
                {} as { [key: string]: IGLTFToFlowGraphMappingObject }
            ),
        },
        outputs: {
            values: {
                value: { name: "value", typeSourceInput: inputs[0] },
            },
        },
        extraProcessor(gltfBlock, _declaration, _mapping, _parser, serializedObjects) {
            if (inferType) {
                // configure it to work the way glTF specifies
                serializedObjects[0].config ||= {};
                serializedObjects[0].config.preventIntegerFloatArithmetic = true;
                // try to infer the type or fallback to Integer
                // check the gltf block for the inputs, see if they have a type
                let type = -1;
                Object.keys(gltfBlock.values || {}).find((value) => {
                    if (gltfBlock.values?.[value].type !== undefined) {
                        type = gltfBlock.values[value].type;
                        return true;
                    }
                    return false;
                });
                if (type !== -1) {
                    serializedObjects[0].config.type = _parser.arrays.types[type].flowGraphType;
                }
            }
            return serializedObjects;
        },
        validation(gltfBlock) {
            if (inferType) {
                // make sure types are the same
                return ValidateTypes(gltfBlock);
            }
            return { valid: true };
        },
    };
}

function ValidateTypes(gltfBlock: IKHRInteractivity_Node): { valid: boolean; error?: string } {
    if (gltfBlock.values) {
        const types = Object.keys(gltfBlock.values)
            .map((key) => gltfBlock.values![key].type)
            .filter((type) => type !== undefined);
        const allSameType = types.every((type) => type === types[0]);
        if (!allSameType) {
            return { valid: false, error: "All inputs must be of the same type" };
        }
    }
    return { valid: true };
}

/**
 * Returns whether a literal path segment has an odd number of consecutive occurrences of any bracket character.
 * Brackets used literally inside a path segment must be doubled, so an odd run means the segment is malformed.
 * @param segment the literal path segment
 * @returns true when the segment contains an odd run of brackets
 */
function HasOddBracketRun(segment: string): boolean {
    for (let index = 0; index < segment.length; index++) {
        const character = segment[index];
        if ("[]{}".indexOf(character) === -1) {
            continue;
        }

        let runLength = 1;
        while (segment[index + runLength] === character) {
            runLength++;
        }
        if (runLength % 2 !== 0) {
            return true;
        }
        index += runLength - 1;
    }
    return false;
}

/**
 * Result of parsing a ratified `debug/log` message template.
 */
export interface IDebugLogTemplateParseResult {
    /** Whether all literal braces are doubled and every parameter is well formed. */
    valid: boolean;
    /** Unique parameter socket ids in first-occurrence order. */
    sockets: string[];
}

/**
 * Parses a ratified `debug/log` message template.
 * Literal braces must be doubled; non-empty text inside a single brace pair defines a socket id.
 * @param message message template to parse
 * @returns template validity and its exact dynamic socket ids
 */
export function ParseDebugLogTemplate(message: string): IDebugLogTemplateParseResult {
    let state = 0;
    let parameterStart = -1;
    const sockets: string[] = [];
    const seen = new Set<string>();
    for (let index = 0; index < message.length; index++) {
        const character = message[index];
        if (character === "{") {
            if (state === 0) {
                state = 1;
            } else if (state === 1) {
                state = 0;
            } else {
                return { valid: false, sockets: [] };
            }
        } else if (character === "}") {
            if (state === 0) {
                state = 3;
            } else if (state === 3) {
                state = 0;
            } else if (state === 2) {
                const socket = message.substring(parameterStart + 1, index);
                if (!seen.has(socket)) {
                    seen.add(socket);
                    sockets.push(socket);
                }
                state = 0;
            } else {
                return { valid: false, sockets: [] };
            }
        } else if (state === 1) {
            parameterStart = index - 1;
            state = 2;
        } else if (state === 3) {
            return { valid: false, sockets: [] };
        }
    }
    return { valid: state === 0, sockets: state === 0 ? sockets : [] };
}

/**
 * Validates the `pointer` configuration value of a `pointer/*` operation, following the JSON Pointer Template
 * Parsing steps of the KHR_interactivity specification. A template parameter must span an entire path segment, so
 * a template such as `/materials/{materialRef}pbrMetallicRoughness/...` is a syntax error, and the specification
 * requires the whole behavior graph to be rejected.
 * @param gltfBlock the glTF interactivity node
 * @param reservedSocketIds input ids owned by the operation rather than template parameters
 * @returns the validation result
 */
function ValidateJsonPointerTemplate(gltfBlock: IKHRInteractivity_Node, reservedSocketIds: ReadonlySet<string> = new Set()): { valid: boolean; error?: string } {
    const pointer = gltfBlock.configuration?.pointer?.value?.[0];
    if (typeof pointer !== "string") {
        return { valid: false, error: "A pointer operation requires a string `pointer` configuration value" };
    }
    // A JSON Pointer (RFC 6901) is either empty or a sequence of `/`-prefixed reference tokens.
    if (pointer.length !== 0 && pointer[0] !== "/") {
        return { valid: false, error: `The JSON Pointer Template "${pointer}" is not a syntactically valid JSON Pointer` };
    }

    const invalid = (reason: string) => ({ valid: false, error: `The JSON Pointer Template "${pointer}" is invalid: ${reason}` });
    const socketIds = new Set<string>();
    for (const segment of pointer.split("/")) {
        if (/~(?:[^01]|$)/.test(segment)) {
            return invalid(`the path segment "${segment}" contains an invalid RFC 6901 escape`);
        }
        const isIntegerParameter = segment[0] === "[" && segment[1] !== "[";
        const isReferenceParameter = segment[0] === "{" && segment[1] !== "{";
        if (!isIntegerParameter && !isReferenceParameter) {
            if (HasOddBracketRun(segment)) {
                return invalid(`the path segment "${segment}" contains an odd number of consecutive brackets`);
            }
            continue;
        }

        const closingBracket = isIntegerParameter ? "]" : "}";
        const body = segment.substring(1, segment.length - 1);
        if (segment.length < 3 || segment[segment.length - 1] !== closingBracket || /[[{]/.test(body) || /[\]}]/.test(body)) {
            return invalid(`the template parameter path segment "${segment}" is malformed`);
        }

        const socketId = body.replace(/~1/g, "/").replace(/~0/g, "~");
        if (reservedSocketIds.has(socketId)) {
            return invalid(`the template parameter "${socketId}" uses a reserved input socket id`);
        }
        if (socketIds.has(socketId)) {
            return invalid(`the template parameter "${socketId}" is used more than once`);
        }
        socketIds.add(socketId);
    }

    return { valid: true };
}

export function getAllSupportedNativeNodeTypes(): string[] {
    return Object.keys(gltfToFlowGraphMapping);
}

/**
 * 
 * These are the nodes from the specs:

### Math Nodes
1. **Constants**
   - E (`math/E`) FlowGraphBlockNames.E
   - Pi (`math/Pi`) FlowGraphBlockNames.PI
   - Infinity (`math/Inf`) FlowGraphBlockNames.Inf
   - Not a Number (`math/NaN`) FlowGraphBlockNames.NaN
2. **Arithmetic Nodes**
   - Absolute Value (`math/abs`) FlowGraphBlockNames.Abs
   - Sign (`math/sign`) FlowGraphBlockNames.Sign
   - Truncate (`math/trunc`) FlowGraphBlockNames.Trunc
   - Floor (`math/floor`) FlowGraphBlockNames.Floor
   - Ceil (`math/ceil`) FlowGraphBlockNames.Ceil
   - Round (`math/round`)  FlowGraphBlockNames.Round
   - Fraction (`math/fract`) FlowGraphBlockNames.Fract
   - Negation (`math/neg`) FlowGraphBlockNames.Negation
   - Addition (`math/add`) FlowGraphBlockNames.Add
   - Subtraction (`math/sub`) FlowGraphBlockNames.Subtract
   - Multiplication (`math/mul`) FlowGraphBlockNames.Multiply
   - Division (`math/div`) FlowGraphBlockNames.Divide
   - Remainder (`math/rem`) FlowGraphBlockNames.Modulo
   - Minimum (`math/min`) FlowGraphBlockNames.Min
   - Maximum (`math/max`) FlowGraphBlockNames.Max
   - Clamp (`math/clamp`) FlowGraphBlockNames.Clamp
   - Saturate (`math/saturate`) FlowGraphBlockNames.Saturate
   - Interpolate (`math/mix`) FlowGraphBlockNames.MathInterpolation
3. **Comparison Nodes**
   - Equality (`math/eq`) FlowGraphBlockNames.Equality
   - Less Than (`math/lt`) FlowGraphBlockNames.LessThan
   - Less Than Or Equal To (`math/le`) FlowGraphBlockNames.LessThanOrEqual
   - Greater Than (`math/gt`) FlowGraphBlockNames.GreaterThan
   - Greater Than Or Equal To (`math/ge`) FlowGraphBlockNames.GreaterThanOrEqual
4. **Special Nodes**
   - Is Not a Number (`math/isNaN`) FlowGraphBlockNames.IsNaN
   - Is Infinity (`math/isInf`) FlowGraphBlockNames.IsInfinity
   - Select (`math/select`) FlowGraphBlockNames.Conditional
   - Switch (`math/switch`) FlowGraphBlockNames.DataSwitch
   - Random (`math/random`) FlowGraphBlockNames.Random
5. **Angle and Trigonometry Nodes**
   - Degrees-To-Radians (`math/rad`) FlowGraphBlockNames.DegToRad
   - Radians-To-Degrees (`math/deg`) FlowGraphBlockNames.RadToDeg
   - Sine (`math/sin`)  FlowGraphBlockNames.Sin
   - Cosine (`math/cos`) FlowGraphBlockNames.Cos
   - Tangent (`math/tan`) FlowGraphBlockNames.Tan
   - Arcsine (`math/asin`) FlowGraphBlockNames.Asin
   - Arccosine (`math/acos`) FlowGraphBlockNames.Acos
   - Arctangent (`math/atan`) FlowGraphBlockNames.Atan
   - Arctangent 2 (`math/atan2`) FlowGraphBlockNames.Atan2
6. **Hyperbolic Nodes**
   - Hyperbolic Sine (`math/sinh`) FlowGraphBlockNames.Sinh
   - Hyperbolic Cosine (`math/cosh`) FlowGraphBlockNames.Cosh
   - Hyperbolic Tangent (`math/tanh`) FlowGraphBlockNames.Tanh
   - Inverse Hyperbolic Sine (`math/asinh`) FlowGraphBlockNames.Asinh
   - Inverse Hyperbolic Cosine (`math/acosh`) FlowGraphBlockNames.Acosh
   - Inverse Hyperbolic Tangent (`math/atanh`) FlowGraphBlockNames.Atanh
7. **Exponential Nodes**
   - Exponent (`math/exp`) FlowGraphBlockNames.Exponential
   - Natural Logarithm (`math/log`) FlowGraphBlockNames.Log
   - Base-2 Logarithm (`math/log2`) FlowGraphBlockNames.Log2
   - Base-10 Logarithm (`math/log10`) FlowGraphBlockNames.Log10
   - Square Root (`math/sqrt`) FlowGraphBlockNames.SquareRoot
   - Cube Root (`math/cbrt`) FlowGraphBlockNames.CubeRoot
   - Power (`math/pow`) FlowGraphBlockNames.Power
8. **Vector Nodes**
   - Length (`math/length`) FlowGraphBlockNames.Length
   - Normalize (`math/normalize`) FlowGraphBlockNames.Normalize
   - Dot Product (`math/dot`) FlowGraphBlockNames.Dot
   - Cross Product (`math/cross`) FlowGraphBlockNames.Cross
   - Rotate 2D (`math/rotate2D`) FlowGraphBlockNames.Rotate2D
   - Rotate 3D (`math/rotate3D`) FlowGraphBlockNames.Rotate3D
   - Transform (`math/transform`) FlowGraphBlockNames.TransformVector
9. **Matrix Nodes**
   - Transpose (`math/transpose`) FlowGraphBlockNames.Transpose
   - Determinant (`math/determinant`) FlowGraphBlockNames.Determinant
   - Inverse (`math/inverse`) FlowGraphBlockNames.InvertMatrix
   - Multiplication (`math/matMul`) FlowGraphBlockNames.MatrixMultiplication
   - Compose (`math/matCompose`) FlowGraphBlockNames.MatrixCompose
   - Decompose (`math/matDecompose`) FlowGraphBlockNames.MatrixDecompose
10. **Quaternion Nodes**
    - Conjugate (`math/quatConjugate`) FlowGraphBlockNames.Conjugate
    - Multiplication (`math/quatMul`) FlowGraphBlockNames.Multiply
    - Angle Between Quaternions (`math/quatAngleBetween`) FlowGraphBlockNames.AngleBetween
    - Quaternion From Axis Angle (`math/quatFromAxisAngle`) FlowGraphBlockNames.QuaternionFromAxisAngle
    - Quaternion To Axis Angle (`math/quatToAxisAngle`) FlowGraphBlockNames.QuaternionToAxisAngle
    - Quaternion From Two Directional Vectors (`math/quatFromDirections`) FlowGraphBlockNames.QuaternionFromDirections
11. **Swizzle Nodes**
    - Combine (`math/combine2`, `math/combine3`, `math/combine4`, `math/combine2x2`, `math/combine3x3`, `math/combine4x4`)
        FlowGraphBlockNames.CombineVector2, FlowGraphBlockNames.CombineVector3, FlowGraphBlockNames.CombineVector4
        FlowGraphBlockNames.CombineMatrix2D, FlowGraphBlockNames.CombineMatrix3D, FlowGraphBlockNames.CombineMatrix
    - Extract (`math/extract2`, `math/extract3`, `math/extract4`, `math/extract2x2`, `math/extract3x3`, `math/extract4x4`)
        FlowGraphBlockNames.ExtractVector2, FlowGraphBlockNames.ExtractVector3, FlowGraphBlockNames.ExtractVector4
        FlowGraphBlockNames.ExtractMatrix2D, FlowGraphBlockNames.ExtractMatrix3D, FlowGraphBlockNames.ExtractMatrix
12. **Integer Arithmetic Nodes**
    - Absolute Value (`math/abs`) FlowGraphBlockNames.Abs
    - Sign (`math/sign`) FlowGraphBlockNames.Sign
    - Negation (`math/neg`) FlowGraphBlockNames.Negation
    - Addition (`math/add`) FlowGraphBlockNames.Add
    - Subtraction (`math/sub`) FlowGraphBlockNames.Subtract
    - Multiplication (`math/mul`) FlowGraphBlockNames.Multiply
    - Division (`math/div`) FlowGraphBlockNames.Divide
    - Remainder (`math/rem`) FlowGraphBlockNames.Modulo
    - Minimum (`math/min`) FlowGraphBlockNames.Min
    - Maximum (`math/max`) FlowGraphBlockNames.Max
    - Clamp (`math/clamp`) FlowGraphBlockNames.Clamp
13. **Integer Comparison Nodes**
    - Equality (`math/eq`) FlowGraphBlockNames.Equality
    - Less Than (`math/lt`) FlowGraphBlockNames.LessThan
    - Less Than Or Equal To (`math/le`) FlowGraphBlockNames.LessThanOrEqual
    - Greater Than (`math/gt`) FlowGraphBlockNames.GreaterThan
    - Greater Than Or Equal To (`math/ge`) FlowGraphBlockNames.GreaterThanOrEqual
14. **Integer Bitwise Nodes**
    - Bitwise NOT (`math/not`) FlowGraphBlockNames.BitwiseNot
    - Bitwise AND (`math/and`) FlowGraphBlockNames.BitwiseAnd
    - Bitwise OR (`math/or`) FlowGraphBlockNames.BitwiseOr
    - Bitwise XOR (`math/xor`) FlowGraphBlockNames.BitwiseXor
    - Right Shift (`math/asr`) FlowGraphBlockNames.BitwiseRightShift
    - Left Shift (`math/lsl`) FlowGraphBlockNames.BitwiseLeftShift
    - Count Leading Zeros (`math/clz`) FlowGraphBlockNames.LeadingZeros
    - Count Trailing Zeros (`math/ctz`) FlowGraphBlockNames.TrailingZeros
    - Count One Bits (`math/popcnt`) FlowGraphBlockNames.OneBitsCounter
15. **Boolean Arithmetic Nodes**
    - Equality (`math/eq`) FlowGraphBlockNames.Equality
    - Boolean NOT (`math/not`) FlowGraphBlockNames.BitwiseNot
    - Boolean AND (`math/and`) FlowGraphBlockNames.BitwiseAnd
    - Boolean OR (`math/or`) FlowGraphBlockNames.BitwiseOr
    - Boolean XOR (`math/xor`) FlowGraphBlockNames.BitwiseXor

### Type Conversion Nodes
1. **Boolean Conversion Nodes**
   - Boolean to Integer (`type/boolToInt`) FlowGraphBlockNames.BooleanToInt
   - Boolean to Float (`type/boolToFloat`) FlowGraphBlockNames.BooleanToFloat
2. **Integer Conversion Nodes**
   - Integer to Boolean (`type/intToBool`) FlowGraphBlockNames.IntToBoolean
   - Integer to Float (`type/intToFloat`) FlowGraphBlockNames.IntToFloat
3. **Float Conversion Nodes**
   - Float to Boolean (`type/floatToBool`) FlowGraphBlockNames.FloatToBoolean
   - Float to Integer (`type/floatToInt`) FlowGraphBlockNames.FloatToInt

### Control Flow Nodes
1. **Sync Nodes**
   - Sequence (`flow/sequence`) FlowGraphBlockNames.Sequence
   - Branch (`flow/branch`) FlowGraphBlockNames.Branch
   - Switch (`flow/switch`) FlowGraphBlockNames.Switch
   - While Loop (`flow/while`) FlowGraphBlockNames.WhileLoop
   - For Loop (`flow/for`) FlowGraphBlockNames.ForLoop
   - Do N (`flow/doN`) FlowGraphBlockNames.DoN
   - Multi Gate (`flow/multiGate`) FlowGraphBlockNames.MultiGate
   - Wait All (`flow/waitAll`) FlowGraphBlockNames.WaitAll
   - Throttle (`flow/throttle`) FlowGraphBlockNames.Throttle
2. **Delay Nodes**
   - Set Delay (`flow/setDelay`) FlowGraphBlockNames.SetDelay
   - Cancel Delay (`flow/cancelDelay`) FlowGraphBlockNames.CancelDelay

### State Manipulation Nodes
1. **Custom Variable Access**
   - Variable Get (`variable/get`) FlowGraphBlockNames.GetVariable
   - Variable Set (`variable/set`) FlowGraphBlockNames.SetVariable
   - Variable Interpolate (`variable/interpolate`)
2. **Object Model Access** // TODO fully test this!!!
   - JSON Pointer Template Parsing (`pointer/get`) [FlowGraphBlockNames.GetProperty, FlowGraphBlockNames.JsonPointerParser]
   - Effective JSON Pointer Generation (`pointer/set`) [FlowGraphBlockNames.SetProperty, FlowGraphBlockNames.JsonPointerParser]
   - Pointer Get (`pointer/get`) [FlowGraphBlockNames.GetProperty, FlowGraphBlockNames.JsonPointerParser]
   - Pointer Set (`pointer/set`) [FlowGraphBlockNames.SetProperty, FlowGraphBlockNames.JsonPointerParser]
   - Pointer Interpolate (`pointer/interpolate`) [FlowGraphBlockNames.ValueInterpolation, FlowGraphBlockNames.JsonPointerParser, FlowGraphBlockNames.PlayAnimation, FlowGraphBlockNames.BezierCurveEasing]

### Animation Control Nodes
1. **Animation Play** (`animation/start`) FlowGraphBlockNames.PlayAnimation
2. **Animation Stop** (`animation/stop`) FlowGraphBlockNames.StopAnimation 
3. **Animation Stop At** (`animation/stopAt`) FlowGraphBlockNames.StopAnimation 

### Event Nodes
1. **Lifecycle Event Nodes**
   - On Start (`event/onStart`) FlowGraphBlockNames.SceneReadyEvent
   - On Tick (`event/onTick`) FlowGraphBlockNames.SceneTickEvent
2. **Custom Event Nodes**
   - Receive (`event/receive`) FlowGraphBlockNames.ReceiveCustomEvent
   - Send (`event/send`) FlowGraphBlockNames.SendCustomEvent

 */
